import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { X, Check, UserMinus } from "lucide-react";
import { Button, Field, Textarea } from "@/design/components";
import { useRemoveProjectMember } from "@/domain/useOnboarding";

// "Remove member" - soft-revokes a project membership (cascade.removeMember,
// server-side). Structurally the simplest of the team-management modals
// (AddMembersModal/AssignLeadContractorModal) since there's nothing to
// search or pick - the target is already known from the row the action was
// opened from. Justification is required, matching the same governed-action
// pattern AssignLeadContractorModal uses.
export function RemoveMemberModal({
  open,
  onOpenChange,
  projectId,
  membershipId,
  memberName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number | string;
  membershipId: number | null;
  memberName: string;
}) {
  const { removeMember, isPending, error } = useRemoveProjectMember();
  const queryClient = useQueryClient();
  const [justification, setJustification] = useState("");
  const [submitted, setSubmitted] = useState(false);

  function reset() {
    setJustification("");
    setSubmitted(false);
  }

  async function onConfirm() {
    if (!membershipId || !justification.trim()) return;
    const ok = await removeMember(membershipId, justification.trim());
    if (ok) {
      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ["project-members", projectId] });
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {submitted ? "Member removed" : "Remove member"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {submitted ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span><strong>{memberName}</strong> has been removed from this project and notified.</span>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                Remove <strong>{memberName}</strong> from this project. They'll be notified with the reason below, and lose access to project actions immediately.
              </Dialog.Description>

              <Field label="Justification">
                <Textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={3}
                  placeholder="Why is this person being removed?"
                />
              </Field>

              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {!submitted && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button variant="danger" disabled={!justification.trim() || isPending} onClick={onConfirm}>
                <UserMinus className="h-4 w-4" /> {isPending ? "Removing…" : "Remove"}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
